/* Pure z-order helpers behind the Layers pane and the Arrange bar's
 * Bring to front / Send to back / Forward / Backward.
 *
 * Stacking is persisted in widget.layout.z. The renderer paints by z and
 * breaks ties by position in the widgets list, so a page's order is
 * "sorted by (z, list index)". Every re-order RENUMBERS z to 1..N in that
 * order -- no gaps, no duplicates -- which keeps the result stable no matter
 * what the old (often all-1) values were. */
import type { SPage, SWidget } from "./model";

export type StackOp = "front" | "back" | "forward" | "backward";

/** Widget ids from back (first) to front (last). */
export function stackOrder(page: SPage): string[] {
  return page.widgets
    .map((w, i) => ({ id: w.id, z: w.layout.z ?? 1, i }))
    .sort((a, b) => a.z - b.z || a.i - b.i)
    .map((x) => x.id);
}

/** Rank (1 = back) of every widget, as used for the canvas overlay z-index. */
export function stackRanks(page: SPage): Map<string, number> {
  return new Map(stackOrder(page).map((id, i) => [id, i + 1]));
}

/** Rewrite z so the given back-to-front list is the stacking order. Returns the same page when nothing changes. */
export function setStackOrder(page: SPage, orderedIds: string[]): SPage {
  const known = new Set(page.widgets.map((w) => w.id));
  const seen = new Set<string>();
  const order: string[] = [];
  for (const id of orderedIds) {
    if (known.has(id) && !seen.has(id)) {
      seen.add(id);
      order.push(id);
    }
  }
  for (const id of stackOrder(page)) if (!seen.has(id)) order.push(id);
  const z = new Map(order.map((id, i) => [id, i + 1]));
  let changed = false;
  const widgets = page.widgets.map((w): SWidget => {
    const nz = z.get(w.id) ?? 1;
    if (w.layout.z === nz) return w;
    changed = true;
    return { ...w, layout: { ...w.layout, z: nz } };
  });
  return changed ? { ...page, widgets } : page;
}

/** Move the selected widgets in the stack. Keeps their relative order. */
export function restack(page: SPage, ids: string[], op: StackOp): SPage {
  const sel = new Set(ids);
  const order = stackOrder(page);
  if (!order.some((id) => sel.has(id))) return page;
  let next: string[];
  if (op === "front") next = [...order.filter((id) => !sel.has(id)), ...order.filter((id) => sel.has(id))];
  else if (op === "back") next = [...order.filter((id) => sel.has(id)), ...order.filter((id) => !sel.has(id))];
  else {
    next = [...order];
    if (op === "forward") {
      for (let i = next.length - 2; i >= 0; i--) if (sel.has(next[i]) && !sel.has(next[i + 1])) [next[i], next[i + 1]] = [next[i + 1], next[i]];
    } else {
      for (let i = 1; i < next.length; i++) if (sel.has(next[i]) && !sel.has(next[i - 1])) [next[i], next[i - 1]] = [next[i - 1], next[i]];
    }
  }
  return setStackOrder(page, next);
}

/** Layers drag-and-drop: put `id` just in front of / behind `targetId`. */
export function moveRelative(page: SPage, id: string, targetId: string, where: "front" | "behind"): SPage {
  if (id === targetId) return page;
  const order = stackOrder(page).filter((x) => x !== id);
  const at = order.indexOf(targetId);
  if (at < 0) return page;
  order.splice(where === "front" ? at + 1 : at, 0, id);
  return setStackOrder(page, order);
}

/** New widgets (add / duplicate / paste) go on top. */
export function topZ(page: SPage): number {
  return page.widgets.reduce((m, w) => Math.max(m, w.layout.z ?? 1), 0) + 1;
}
